import React, { useState, useEffect, useCallback } from 'react';
import {
  PayPalScriptProvider,
  PayPalButtons,
  PayPalCardFieldsProvider,
  PayPalCardFieldsForm,
  usePayPalCardFields,
} from '@paypal/react-paypal-js';
import {
  X,
  Crown,
  Sparkles,
  Shield,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Zap,
  Lock,
  Unlock,
  Coins,
  Calendar,
  Layers,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Check,
  Clock,
  Receipt,
  Building2,
  ChevronRight,
  Copy,
  UploadCloud,
  FileCheck,
  Eye,
  Hourglass,
  Landmark,
  Package,
  Star,
  Activity
} from 'lucide-react';

interface GeoPricingTier {
  title: string;
  description: string;
  amount: number;
  amountUsd: number;
  displayFormatted: string;
  billingType: string;
}

interface GeoPricingResponse {
  success: boolean;
  country: string;
  isSriLanka: boolean;
  currency: 'LKR' | 'USD';
  currencySymbol: string;
  paypalClientId?: string;
  pricing: {
    setupLicense: GeoPricingTier;
    whiteLabelAddon: GeoPricingTier;
    completeProBundle: GeoPricingTier;
    maintenanceMonthly: GeoPricingTier;
  };
}

interface AccountStatusResponse {
  success: boolean;
  tenantId: number;
  tenantName: string;
  apiKey: string;
  status: string;
  isWhiteLabelEnabled: boolean;
  plan: string;
  planName: string;
  isUnlimited: boolean;
  isSetupFeePaid?: boolean;
  agentTokensTotal: number;
  agentTokensUsed: number;
  agentTokensRemaining: number | string;
  subscriptionStatus: 'active' | 'grace_period' | 'suspended' | 'trial';
  monthlyDueDate: string | null;
  daysUntilDue: number | null;
  isSuspended: boolean;
  pendingSlip?: any;
  transactions: any[];
}

interface TenetAccountBillingModalProps {
  isOpen: boolean;
  isFullPage?: boolean;
  onClose: () => void;
  token?: string;
  tenantApiKey?: string;
  initialTenant?: any;
  initialTab?: 'plans' | 'bank_transfer' | 'invoices' | 'token_history';
  onPlanUpdated?: (updatedTenant: any) => void;
}

function InlineCardSubmitButton({
  amountUsd,
  planTitle,
  onDone,
  onError,
}: {
  amountUsd: number;
  planTitle: string;
  onDone: () => void;
  onError: (err: any) => void;
}) {
  const { cardFieldsForm } = usePayPalCardFields();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!cardFieldsForm) return;
    setSubmitting(true);
    setError('');
    try {
      await cardFieldsForm.submit();
      onDone();
    } catch (err: any) {
      setError(err?.message || 'Card payment failed. Please check your card number, CVV, and expiry date.');
      onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !cardFieldsForm}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.12] text-white font-mono text-xs font-bold transition-all shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15)] disabled:opacity-50 cursor-pointer"
      >
        {submitting ? (
          <>
            <RefreshCw size={14} className="animate-spin text-cyan-400" />
            <span>Authorizing Card Payment...</span>
          </>
        ) : (
          <>
            <CreditCard size={14} className="text-cyan-400" />
            <span className="font-tabular">Pay ${amountUsd.toFixed(2)} USD via Card</span>
          </>
        )}
      </button>
      {error && <p className="text-rose-400 text-xs mt-1.5 font-mono">{error}</p>}
    </div>
  );
}

export const TenetAccountBillingModal: React.FC<TenetAccountBillingModalProps> = ({
  isOpen,
  isFullPage = true,
  onClose,
  token,
  tenantApiKey,
  initialTenant,
  initialTab,
  onPlanUpdated,
}) => {
  const [activeTab, setActiveTab] = useState<'plans' | 'bank_transfer' | 'invoices' | 'token_history'>(initialTab || 'plans');
  const [loading, setLoading] = useState<boolean>(true);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatusResponse | null>(null);
  const [tokenHistory, setTokenHistory] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [geoPricing, setGeoPricing] = useState<GeoPricingResponse | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<'LKR' | 'USD'>('LKR');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [inlineCheckoutPlan, setInlineCheckoutPlan] = useState<string | null>(null);
  const [paypalClientId, setPaypalClientId] = useState<string>('');

  // Fetch PayPal Client ID on modal load
  useEffect(() => {
    fetch('/api/tenet/billing/paypal/config')
      .then(r => r.json())
      .then(d => {
        if (d.paypalClientId) setPaypalClientId(d.paypalClientId);
      })
      .catch(() => {});
  }, []);

  // Bank Transfer States
  const [bankDetails, setBankDetails] = useState<any>({
    bankName: 'Commercial Bank of Ceylon',
    accountName: 'Kangavena Cloud (Pvt) Ltd',
    accountNumber: '1000582910',
    branch: 'Colombo 03 (Colpetty Branch)',
    swiftCode: 'COMBLLKX',
    currency: 'LKR',
    referenceGuidance: 'Please include your Business Name or Registered Mobile as the deposit/transfer remark.',
    supportPhone: '+94 76 711 4888'
  });
  const [selectedBankPlan, setSelectedBankPlan] = useState<'full_bundle' | 'setup_license' | 'white_label' | 'monthly_subscription'>('full_bundle');
  const [depositorName, setDepositorName] = useState<string>('');
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string | null>(null);
  const [slipNotes, setSlipNotes] = useState<string>('');
  const [isSubmittingSlip, setIsSubmittingSlip] = useState<boolean>(false);
  const [copiedAcc, setCopiedAcc] = useState<boolean>(false);

  const activeToken = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_client_token') || localStorage.getItem('tenet_auth_token') : null);

  const getHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (activeToken) h['Authorization'] = `Bearer ${activeToken}`;
    if (tenantApiKey) h['x-tenant-api-key'] = tenantApiKey;
    return h;
  }, [activeToken, tenantApiKey]);

  const fetchTokenHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch('/api/tenet/account/token-history', { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data && data.success) {
          setTokenHistory(data);
        }
      }
    } catch (err) {
      console.error('[BillingModal] Error fetching token history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [getHeaders]);

  const loadData = useCallback(async (currencyOverride?: 'LKR' | 'USD') => {
    setLoading(true);
    try {
      const cur = currencyOverride || selectedCurrency;
      const [pricingRes, statusRes, bankRes, historyRes] = await Promise.all([
        fetch(`/api/tenet/billing/geo-pricing?currency=${cur}`),
        fetch('/api/tenet/account/status', { headers: getHeaders() }),
        fetch('/api/tenet/billing/bank-details').catch(() => null),
        fetch('/api/tenet/account/token-history', { headers: getHeaders() }).catch(() => null),
      ]);

      const pricingData = await pricingRes.json();
      const statusData = await statusRes.json();

      if (pricingData.success) {
        setGeoPricing(pricingData);
        if (pricingData.paypalClientId) {
          setPaypalClientId(pricingData.paypalClientId);
        }
        if (!currencyOverride) {
          setSelectedCurrency(pricingData.currency);
        }
      }
      if (statusData.success) {
        setAccountStatus(statusData);
      }
      if (bankRes && bankRes.ok) {
        const bData = await bankRes.json();
        if (bData.success && bData.bankDetails) {
          setBankDetails(bData.bankDetails);
        }
      }
      if (historyRes && historyRes.ok) {
        const hData = await historyRes.json();
        if (hData && hData.success) {
          setTokenHistory(hData);
        }
      }
    } catch (err: any) {
      console.error('[BillingModal] Error loading billing data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCurrency, getHeaders]);

  const handleCopyAccountNumber = () => {
    if (navigator?.clipboard && bankDetails?.accountNumber) {
      navigator.clipboard.writeText(bankDetails.accountNumber);
      setCopiedAcc(true);
      setTimeout(() => setCopiedAcc(false), 2500);
    }
  };

  const handleSlipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 12 * 1024 * 1024) {
        setActionMessage({ type: 'error', text: 'File size exceeds 12MB limit. Please upload a smaller image or compressed PDF.' });
        return;
      }
      setSlipFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSlipPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBankSlipSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slipPreview && (!referenceNumber || !referenceNumber.trim())) {
      setActionMessage({ type: 'error', text: 'Please upload a bank deposit slip photo or enter your bank transaction reference number.' });
      return;
    }

    setIsSubmittingSlip(true);
    setActionMessage(null);

    if (selectedBankPlan === 'monthly_subscription' && !isSetupFeePaid) {
      setActionMessage({
        type: 'error',
        text: 'One-Time Setup License Required: Free trial accounts must purchase the Kangavena Setup License (or Full Bundle) before subscribing to monthly cloud maintenance.'
      });
      return;
    }

    try {
      let amount = '15,000';
      if (selectedBankPlan === 'setup_license') amount = '10,000';
      if (selectedBankPlan === 'white_label') amount = '5,000';
      if (selectedBankPlan === 'monthly_subscription') amount = '2,500';

      const formData = new FormData();
      formData.append('planType', selectedBankPlan);
      formData.append('amount', amount);
      formData.append('currency', 'LKR');
      formData.append('depositorName', depositorName || accountStatus?.tenantName || '');
      formData.append('referenceNumber', referenceNumber);
      formData.append('notes', slipNotes);
      if (slipFile) {
        formData.append('slipImage', slipFile);
      } else if (slipPreview && slipPreview.startsWith('data:')) {
        formData.append('base64Slip', slipPreview);
      }

      const res = await fetch('/api/tenet/billing/bank-transfer/submit', {
        method: 'POST',
        headers: {
          ...(activeToken ? { 'Authorization': `Bearer ${activeToken}` } : {}),
          ...(tenantApiKey ? { 'x-tenant-api-key': tenantApiKey } : {}),
        },
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        setActionMessage({
          type: 'success',
          text: '🎉 Bank transfer slip submitted successfully! Our finance team will review and activate your account within 15–30 minutes.'
        });
        setSlipFile(null);
        setSlipPreview(null);
        setReferenceNumber('');
        setSlipNotes('');
        await loadData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to submit deposit slip' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Error submitting deposit slip.' });
    } finally {
      setIsSubmittingSlip(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, loadData]);

  // Keyboard shortcut listener for ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle URL return after PayPal capture
  useEffect(() => {
    if (!isOpen) return;
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('token') || params.get('orderId');
    const planType = params.get('plan') || params.get('planType');
    const paymentSuccess = params.get('payment_success') === '1' || params.get('payment') === 'success';

    if (paymentSuccess && orderId && planType) {
      capturePayPalOrder(orderId, planType);
    }
  }, [isOpen]);

  const capturePayPalOrder = async (orderId: string, planType: string) => {
    setProcessingPlan(planType);
    try {
      const res = await fetch('/api/tenet/billing/paypal/capture-order', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ orderId, planType }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMessage({
          type: 'success',
          text: `Payment Successful! Your ${data.tenant?.planType === 'pro' ? 'Pro Unlimited' : planType} membership has been activated.`,
        });
        if (onPlanUpdated && data.tenant) {
          onPlanUpdated(data.tenant);
        }
        loadData();
        // Clean query params from URL cleanly without reloading
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to capture PayPal payment.' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Error communicating with billing server.' });
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleInitiatePayPalCheckout = async (planType: string) => {
    setProcessingPlan(planType);
    setActionMessage(null);
    try {
      const returnUrl = `${window.location.origin}/tenet?payment_success=1&plan=${planType}`;
      const cancelUrl = `${window.location.origin}/tenet?payment_cancelled=1`;

      const res = await fetch('/api/tenet/billing/paypal/create-order', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          planType,
          currency: selectedCurrency,
          returnUrl,
          cancelUrl,
        }),
      });

      const data = await res.json();
      if (data.success && data.approveUrl) {
        window.location.href = data.approveUrl;
      } else {
        setActionMessage({
          type: 'error',
          text: data.error || 'Failed to initialize PayPal order. Please check PayPal credentials.',
        });
        setProcessingPlan(null);
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Network error creating order.' });
      setProcessingPlan(null);
    }
  };

  const handleCurrencyChange = (curr: 'LKR' | 'USD') => {
    setSelectedCurrency(curr);
    loadData(curr);
  };

  const cardFieldStyles = {
    '.card-field': {
      color: '#f8fafc',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: '13px',
      fontWeight: '500',
    },
    '.card-field::placeholder': {
      color: '#64748b',
    },
    '.card-field.invalid': {
      color: '#fb7185',
    },
  };

  const getPlanMeta = (planType: string) => {
    const p = geoPricing?.pricing;
    if (planType === 'setup_license') {
      return {
        id: 'setup_license',
        title: 'Kangavena AI Setup License',
        subtitle: 'Pro Unlimited Agent Tokens & Lifetime Hub Access',
        displayPrice: p?.setupLicense?.displayFormatted || (selectedCurrency === 'LKR' ? 'Rs. 10,000' : '$35.00'),
        amountUsd: p?.setupLicense?.amountUsd || (selectedCurrency === 'LKR' ? 33.30 : 35.00),
        badge: 'PRO UNLIMITED',
        badgeColor: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
        bullets: [
          'Unlimited AI Agent Tokens (Zero Cap)',
          'All visual flow canvas nodes unlocked',
          '24/7 Real-Time Live WhatsApp Voice & Telephony',
          'Includes first 30 days cloud hosting'
        ]
      };
    } else if (planType === 'white_label') {
      return {
        id: 'white_label',
        title: 'White-Label Rebranding Suite',
        subtitle: 'Deploy Custom Brand Name & Avatar Across All Interfaces',
        displayPrice: p?.whiteLabelAddon?.displayFormatted || (selectedCurrency === 'LKR' ? 'Rs. 5,000' : '$18.00'),
        amountUsd: p?.whiteLabelAddon?.amountUsd || (selectedCurrency === 'LKR' ? 16.60 : 18.00),
        badge: 'WHITE LABEL',
        badgeColor: 'border-purple-500/30 bg-purple-500/10 text-purple-300',
        bullets: [
          'Custom Business Name & Sovereign Subdomain',
          'Custom Live Web Avatar & Logo',
          'Zero "Powered by Kangavena" watermarks',
          'Direct customer-facing branded client portal'
        ]
      };
    } else if (planType === 'full_bundle') {
      return {
        id: 'full_bundle',
        title: 'Complete Unlimited + White-Label Bundle',
        subtitle: 'Best Value: Sovereign Pro Unlimited AI + Total White-Label',
        displayPrice: p?.completeProBundle?.displayFormatted || (selectedCurrency === 'LKR' ? 'Rs. 15,000' : '$50.00'),
        amountUsd: p?.completeProBundle?.amountUsd || (selectedCurrency === 'LKR' ? 49.90 : 50.00),
        badge: 'MOST POPULAR — BEST VALUE',
        badgeColor: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
        bullets: [
          'Everything in Setup License (Unlimited Tokens)',
          'Everything in White-Label Suite',
          'VIP Priority WhatsApp & Telephony Gateway',
          'Save over 15% with complete all-in-one bundle'
        ]
      };
    } else {
      return {
        id: 'monthly_subscription',
        title: 'Monthly Cloud AI Subscription',
        subtitle: 'Continuous Luna Neural Core hosting, WhatsApp SIP, memory sync & updates',
        displayPrice: p?.maintenanceMonthly?.displayFormatted || (selectedCurrency === 'LKR' ? 'Rs. 2,500 / mo' : '$9.00 / mo'),
        amountUsd: p?.maintenanceMonthly?.amountUsd || (selectedCurrency === 'LKR' ? 8.30 : 9.00),
        badge: 'MONTHLY CLOUD',
        badgeColor: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
        bullets: [
          'High-performance dedicated Cloud AI runtime',
          'Continuous Luna Neural Multimodal Live session hosting',
          'Automated database backups & vector memory sync',
          'Cancel anytime with zero lock-in contracts'
        ]
      };
    }
  };

  const handleCreateOrderForSdk = async (planType: string): Promise<string> => {
    const returnUrl = `${window.location.origin}/tenet?payment_success=1&plan=${planType}`;
    const cancelUrl = `${window.location.origin}/tenet?payment_cancelled=1`;

    const res = await fetch('/api/tenet/billing/paypal/create-order', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        planType,
        currency: selectedCurrency,
        returnUrl,
        cancelUrl,
      }),
    });

    const data = await res.json();
    if (!data.success || !data.orderId) {
      throw new Error(data.error || 'Failed to initialize PayPal order.');
    }
    return data.orderId;
  };

  const handleCaptureOrderForSdk = async (orderId: string, planType: string) => {
    setProcessingPlan(planType);
    try {
      const res = await fetch('/api/tenet/billing/paypal/capture-order', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ orderId, planType }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMessage({
          type: 'success',
          text: `🎉 Payment Successful! Your ${data.tenant?.planType === 'pro' ? 'Pro Unlimited' : planType} membership has been activated.`,
        });
        if (onPlanUpdated && data.tenant) {
          onPlanUpdated(data.tenant);
        }
        setInlineCheckoutPlan(null);
        await loadData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to capture payment.' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Error communicating with billing server.' });
    } finally {
      setProcessingPlan(null);
    }
  };

  if (!isOpen) return null;

  const isUnlimited = accountStatus?.isUnlimited === true;
  const isWhiteLabel = accountStatus?.isWhiteLabelEnabled === true;
  const isSuspended = accountStatus?.isSuspended === true;
  const inGracePeriod = accountStatus?.subscriptionStatus === 'grace_period';
  const isSetupFeePaid = accountStatus?.isSetupFeePaid === true || isUnlimited;

  const tokensTotal = typeof accountStatus?.agentTokensTotal === 'number' ? accountStatus.agentTokensTotal : 100000;
  const tokensUsed = accountStatus?.agentTokensUsed || 0;
  const tokensRemaining = accountStatus?.agentTokensRemaining !== undefined ? accountStatus.agentTokensRemaining : 100000;
  const tokenPercent = isUnlimited ? 100 : Math.min(100, Math.max(0, Math.round(((tokensTotal - tokensUsed) / tokensTotal) * 100)));

  const pricing = geoPricing?.pricing;
  const currSym = selectedCurrency === 'LKR' ? 'Rs. ' : '$';

  const modalBodyContent = (
    <>
      {/* ── Rule 24 Header Bar: Obsidian Specular Liquid Glass (Stitch Sovereign Spec) ── */}
      <div className="relative px-3.5 sm:px-6 py-3 sm:py-4 border-b border-white/[0.06] bg-[#090a10]/95 backdrop-blur-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 shrink-0 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]">
        <div className="flex items-center gap-3.5">
          {/* Sovereign VIP Cyber Crest */}
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold shadow-[0_0_15px_rgba(245,158,11,0.15)] shrink-0">
            <Zap size={20} className="fill-amber-400 text-amber-400" />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-bold text-base sm:text-lg text-white font-mono tracking-tight flex items-center gap-2">
                <span>Kangavena Sovereign</span>
                <span className="text-slate-600 font-normal">/</span>
                <span className="text-slate-400 font-normal text-sm sm:text-base">Tenet Core</span>
              </h2>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 tracking-wide">
                  {isUnlimited ? 'PRO UNLIMITED' : 'BASIC TRIAL'}
                </span>
                <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 flex items-center gap-1 tracking-wide">
                  <Crown size={11} className="text-amber-400" /> SOVEREIGN VIP TIER
                </span>
                <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border border-white/10 bg-white/[0.04] text-slate-400 tracking-wider">
                  BLACK CARD ACCESS
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-400 font-sans mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <span className="font-mono text-slate-300 text-[11px]">
                Node ID: <strong className="text-white">{accountStatus?.tenantName ? accountStatus.tenantName.toLowerCase().replace(/[^a-z0-9]/g, '') + '.sovereign.vault' : 'test2.sovereign.vault'}</strong>
              </span>
              <span className="text-slate-600 hidden sm:inline">•</span>
              <span className="hidden sm:inline text-slate-400 text-[11px]">Uncapped AI Tokens, White-Label Licensing & PayPal Gateway</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 self-end sm:self-auto">
          {/* Hardware Segmented Currency Switcher (Warm Amber Selected Accent) */}
          <div className="flex items-center p-1 bg-black/60 rounded-xl border border-white/[0.08] text-xs font-mono shadow-[inset_0_1px_2px_rgba(0,0,0,0.6)]">
            <button
              type="button"
              onClick={() => handleCurrencyChange('LKR')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer font-bold text-xs flex items-center gap-1.5 ${
                selectedCurrency === 'LKR' 
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <span>LKR (Rs.)</span>
            </button>
            <button
              type="button"
              onClick={() => handleCurrencyChange('USD')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer font-bold text-xs flex items-center gap-1.5 ${
                selectedCurrency === 'USD' 
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <span>USD ($)</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => loadData()}
            disabled={loading}
            className="p-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-slate-400 hover:text-white transition cursor-pointer shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]"
            title="Refresh Account Status"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin text-amber-400' : ''} />
          </button>

          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] text-slate-400 hover:text-white transition cursor-pointer border border-white/10 text-xs font-mono font-bold flex items-center gap-1.5 shadow-sm"
            title="Close Terminal (Esc)"
          >
            <span>ESC</span>
            <X size={13} />
          </button>
        </div>
      </div>

      {/* ── Sub-header Tab Bar: Specular Liquid Glass Tabs (Stitch Spec) ── */}
      <div className="px-6 pt-2.5 border-b border-white/[0.06] bg-[#0c0e14]/90 flex items-center justify-between gap-4 overflow-x-auto shrink-0">
        <div className="tenet-mob-scrollx flex items-center gap-4 sm:gap-6 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('plans')}
            className={`pb-2.5 text-xs font-mono font-bold transition border-b-2 flex items-center gap-2 cursor-pointer shrink-0 ${
              activeTab === 'plans'
                ? 'border-amber-400 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Package size={14} className={activeTab === 'plans' ? 'text-amber-400' : 'text-slate-500'} />
            <span>Plans & Packages</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('bank_transfer')}
            className={`pb-2.5 text-xs font-mono font-bold transition border-b-2 flex items-center gap-2 cursor-pointer shrink-0 ${
              activeTab === 'bank_transfer'
                ? 'border-amber-400 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Building2 size={14} className={activeTab === 'bank_transfer' ? 'text-amber-400' : 'text-slate-500'} />
            <span>Bank Transfer (Sri Lanka)</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] font-mono font-bold">
              CEFT • LKR
            </span>
            {accountStatus?.pendingSlip && accountStatus.pendingSlip.status === 'pending_verification' && (
              <span className="px-1.5 py-0.2 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[9px] font-mono animate-pulse">
                PENDING
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('invoices')}
            className={`pb-2.5 text-xs font-mono font-bold transition border-b-2 flex items-center gap-2 cursor-pointer shrink-0 ${
              activeTab === 'invoices'
                ? 'border-cyan-400 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Receipt size={14} className={activeTab === 'invoices' ? 'text-cyan-400' : 'text-slate-500'} />
            <span>Receipts & Audit Log</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[9px] font-mono font-bold">
              {accountStatus?.transactions?.length || 1}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('token_history');
              fetchTokenHistory();
            }}
            className={`pb-2.5 text-xs font-mono font-bold transition border-b-2 flex items-center gap-2 cursor-pointer shrink-0 ${
              activeTab === 'token_history'
                ? 'border-cyan-400 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Activity size={14} className={activeTab === 'token_history' ? 'text-cyan-400' : 'text-slate-500'} />
            <span>Token History & Billing Cycles</span>
            <span className="px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[9px] font-mono font-bold">
              AUDIT LEDGER
            </span>
          </button>
        </div>

        {/* Real-time Telemetry Capsule */}
        <div className="hidden lg:flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.02] border border-white/[0.06] text-[11px] font-mono text-slate-400 shrink-0 pb-1 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
          <span>Zero latency cloud architecture & dedicated GPU nodes</span>
        </div>
      </div>

      {/* ── Scrollable Studio Canvas: True Full-Screen Edge-to-Edge Fluid Layout ── */}
      <div className="p-3.5 sm:p-8 lg:px-12 lg:py-8 overflow-y-auto space-y-6 sm:space-y-8 flex-1 text-xs w-full tenet-mobile-scroll">
        
        {/* Action Message Alert */}
        {actionMessage && (
          <div className={`p-4 rounded-2xl flex items-center gap-3 border shadow-lg animate-in fade-in ${
            actionMessage.type === 'success'
              ? 'liquid-glass-card border-emerald-500/40 text-emerald-200 shadow-[inset_0_1px_0_0_rgba(16,185,129,0.2)]'
              : 'liquid-glass-card border-rose-500/40 text-rose-200 shadow-[inset_0_1px_0_0_rgba(244,63,94,0.2)]'
          }`}>
            {actionMessage.type === 'success' ? (
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
            ) : (
              <AlertTriangle size={18} className="text-rose-400 shrink-0" />
            )}
            <span className="font-semibold text-xs leading-relaxed">{actionMessage.text}</span>
          </div>
        )}

        {/* Grace Period / Suspension Notice */}
        {isSuspended && (
          <div className="p-4 rounded-2xl liquid-glass-card border-rose-500/40 text-rose-200 flex items-center gap-3 shadow-[inset_0_1px_0_0_rgba(244,63,94,0.2)] animate-pulse">
            <AlertTriangle size={20} className="text-rose-400 shrink-0" />
            <div>
              <p className="font-bold text-sm">Account Suspended Due to Overdue Subscription</p>
              <p className="text-xs text-rose-300 mt-0.5">Your monthly cloud maintenance fee is past due. Renew below to reactivate WhatsApp live calls and web agent capabilities immediately.</p>
            </div>
          </div>
        )}

        {inGracePeriod && !isSuspended && (
          <div className="p-4 rounded-2xl liquid-glass-card border-amber-500/40 text-amber-200 flex items-center gap-3 shadow-[inset_0_1px_0_0_rgba(245,158,11,0.2)]">
            <Clock size={20} className="text-amber-400 shrink-0" />
            <div>
              <p className="font-bold text-sm">5-Day Grace Period Active</p>
              <p className="text-xs text-amber-300 mt-0.5">Your subscription renewal is due. Services remain active for 5 days. Please pay the monthly maintenance fee to prevent service interruptions.</p>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* 1. STATUS HERO HUD (3 OBSIDIAN RAISED CARDS - STITCH SPEC)      */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Card 1: Subscription Tier */}
          <div className="bg-[#0c0e16] border border-white/[0.08] rounded-2xl p-5 flex flex-col justify-between shadow-sm min-h-[140px] relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest font-semibold">
                CURRENT LICENSE TIER
              </span>
              <span className={`text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border flex items-center gap-1 ${
                isUnlimited 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                  : 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>{isUnlimited ? 'ACTIVE & VERIFIED' : 'ACTIVE TRIAL'}</span>
              </span>
            </div>

            <div className="my-2">
              <div className="text-2xl font-bold text-white tracking-tight">
                {isUnlimited ? 'Pro Unlimited' : 'Basic Trial'}
              </div>
            </div>

            <div className="flex items-end justify-between pt-2 border-t border-white/[0.05] text-xs">
              <span className="text-slate-400 font-sans text-[11px]">VIP Enterprise Instance</span>
              <div className="text-right font-mono">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">RENEWAL / HORIZON</div>
                <div className="text-xs font-semibold text-white font-tabular">
                  {accountStatus?.monthlyDueDate 
                    ? new Date(accountStatus.monthlyDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) 
                    : 'Oct 8, 2026'}
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Agent Token Reserve */}
          <div className="bg-[#0c0e16] border border-white/[0.08] rounded-2xl p-5 flex flex-col justify-between shadow-sm min-h-[140px] relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest font-semibold">
                AGENT TOKEN RESERVE
              </span>
              <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                <Zap size={11} className="fill-amber-400 text-amber-400" />
                <span>∞ UNLIMITED TOKENS</span>
              </span>
            </div>

            <div className="my-2">
              <div className="text-2xl font-bold text-white tracking-tight font-mono font-tabular flex items-center gap-2">
                <span>{isUnlimited ? 'Uncapped' : `${typeof tokensRemaining === 'number' ? tokensRemaining.toLocaleString() : tokensRemaining}`}</span>
                <span className="text-xs font-sans text-slate-400 font-normal">tokens reserve</span>
              </div>
            </div>

            <div className="flex items-end justify-between pt-2 border-t border-white/[0.05] text-xs">
              <span className="text-slate-400 font-sans text-[11px]">Hedge Execution Pipeline</span>
              <span className="text-emerald-400 font-mono font-bold text-[11px]">Zero Throttling (Uncapped)</span>
            </div>
          </div>

          {/* Card 3: Agency Sovereign Branding */}
          <div className="bg-[#0c0e16] border border-white/[0.08] rounded-2xl p-5 flex flex-col justify-between shadow-sm min-h-[140px] relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest font-semibold">
                SOVEREIGN BRANDING
              </span>
              <span className={`text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border flex items-center gap-1 ${
                isWhiteLabel 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                  : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
              }`}>
                {isWhiteLabel ? <Crown size={10} /> : <Lock size={10} />}
                <span>{isWhiteLabel ? 'FULLY UNLOCKED' : 'LOCKED TIER'}</span>
              </span>
            </div>

            <div className="my-2">
              {isWhiteLabel ? (
                <div className="text-lg font-bold text-white flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-400" />
                  <span>Sovereign Identity</span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="text-lg font-bold text-slate-200 flex items-center gap-2">
                    <Lock size={16} className="text-amber-400" />
                    <span>Default Luna AI</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInlineCheckoutPlan('white_label')}
                    className="text-amber-400 hover:text-amber-300 text-xs font-bold font-mono transition cursor-pointer flex items-center gap-1"
                  >
                    <span>Unlock →</span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-end justify-between pt-2 border-t border-white/[0.05] text-xs">
              <span className="text-slate-400 font-sans text-[11px]">Target Identity:</span>
              <span className="text-slate-200 font-mono text-[11px] font-semibold">
                {isWhiteLabel ? 'Custom Organization' : 'Kangavena Brand'}
              </span>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TAB 1: AVAILABLE PLANS & LICENSES                               */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {activeTab === 'plans' && (
          <div className="space-y-6">
            
            {/* Grandfathered / Pro Active Status Banner */}
            {isUnlimited && isWhiteLabel && (
              <div className="liquid-glass-card rounded-2xl p-5 border-emerald-500/30 shadow-[inset_0_1px_0_0_rgba(16,185,129,0.2)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 shrink-0">
                    <CheckCircle2 size={22} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <span>Full Pro Bundle Active</span>
                      <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold">
                        LIFETIME SETUP VERIFIED
                      </span>
                    </h4>
                    <p className="text-xs text-slate-300 mt-0.5">
                      Your account has full Pro Unlimited tokens and White-Label branding rights unlocked. You only pay the monthly cloud maintenance.
                    </p>
                  </div>
                </div>
                {accountStatus?.monthlyDueDate && (
                  <div className="text-right sm:border-l sm:border-white/10 sm:pl-5 shrink-0 font-mono">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">Next Billing Date</div>
                    <div className="text-base font-bold font-tabular text-emerald-300">
                      {new Date(accountStatus.monthlyDueDate).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {inlineCheckoutPlan ? (
              /* ── IN-PAGE DIRECT CREDIT CARD & PAYPAL CHECKOUT STUDIO ── */
              <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
                {/* Navigation Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-white/[0.06]">
                  <button
                    type="button"
                    onClick={() => setInlineCheckoutPlan(null)}
                    className="flex items-center gap-2 text-xs font-mono font-bold text-slate-300 hover:text-white liquid-glass-card liquid-glass-card-interactive px-4 py-2 rounded-xl border border-white/[0.08] transition cursor-pointer shadow-sm w-fit"
                  >
                    <ArrowLeft size={14} className="text-cyan-400" />
                    <span>Back to All Packages</span>
                  </button>

                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[11px] font-mono text-emerald-400 flex items-center gap-1.5 font-semibold">
                      <ShieldCheck size={14} />
                      <span>In-Page 256-Bit SSL Encrypted Checkout</span>
                    </span>
                  </div>
                </div>

                {/* Plan Quick Switcher Tabs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {[
                    { id: 'setup_license', title: 'Setup License', price: pricing?.setupLicense?.displayFormatted || 'Rs. 10,000' },
                    { id: 'white_label', title: 'White-Label', price: pricing?.whiteLabelAddon?.displayFormatted || 'Rs. 5,000' },
                    { id: 'full_bundle', title: 'Full Bundle', price: pricing?.completeProBundle?.displayFormatted || 'Rs. 15,000', badge: 'SAVE 25%' },
                    { id: 'monthly_subscription', title: 'Monthly Cloud', price: pricing?.maintenanceMonthly?.displayFormatted || 'Rs. 2,500' },
                  ].map((item) => {
                    const isSelected = inlineCheckoutPlan === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setInlineCheckoutPlan(item.id)}
                        className={`p-3 rounded-xl border text-left transition cursor-pointer relative ${
                          isSelected
                            ? 'liquid-glass-overlay border-cyan-500/60 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15)]'
                            : 'liquid-glass-card liquid-glass-card-interactive text-slate-400'
                        }`}
                      >
                        {item.badge && (
                          <span className="absolute -top-2 right-2 text-[8px] font-mono font-bold bg-amber-500 text-slate-950 px-1.5 py-0.2 rounded-full">
                            {item.badge}
                          </span>
                        )}
                        <div className={`text-xs font-mono font-bold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {item.title}
                        </div>
                        <div className="text-[11px] font-mono font-tabular text-cyan-300 font-bold">{item.price}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected Plan Details Banner */}
                {(() => {
                  const planMeta = getPlanMeta(inlineCheckoutPlan);
                  return (
                    <div className="liquid-glass-card rounded-2xl p-6 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border uppercase ${planMeta.badgeColor}`}>
                              {planMeta.badge}
                            </span>
                            <span className="text-xs text-slate-400 font-mono">Instant Automated In-Page Card Activation</span>
                          </div>
                          <h3 className="text-lg font-bold text-white mt-1.5">{planMeta.title}</h3>
                          <p className="text-xs text-slate-400">{planMeta.subtitle}</p>
                        </div>

                        <div className="text-left sm:text-right font-mono shrink-0 sm:border-l sm:border-white/10 sm:pl-6">
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Payable Amount</div>
                          <div className="text-2xl font-bold font-tabular text-cyan-300">{planMeta.displayPrice}</div>
                          <div className="text-[11px] text-slate-400 font-tabular">≈ ${planMeta.amountUsd.toFixed(2)} USD</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-4 border-t border-white/[0.06] text-xs text-slate-300">
                        {planMeta.bullets.map((b, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                            <span>{b}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* In-Page Card Form & PayPal Smart Buttons */}
                {(() => {
                  const planMeta = getPlanMeta(inlineCheckoutPlan);
                  const effectiveClientId = paypalClientId || geoPricing?.paypalClientId || '';

                  return effectiveClientId ? (
                    <div className="liquid-glass-card rounded-2xl p-6 space-y-5">
                      <PayPalScriptProvider
                        options={{
                          clientId: effectiveClientId,
                          components: 'buttons,card-fields',
                          'enable-funding': 'card',
                          'disable-funding': 'paylater,venmo',
                        }}
                      >
                        {/* Section A: Direct Credit / Debit Card Form */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                              <CreditCard size={14} className="text-cyan-400" />
                              <span>Pay Directly with Debit / Credit Card (In-Page)</span>
                            </span>
                            <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                              <Shield size={11} className="text-emerald-400" /> Visa / Mastercard / Amex
                            </span>
                          </div>

                          <div className="bg-black/40 border border-white/[0.08] rounded-xl p-4 shadow-[inset_0_1px_2px_rgba(0,0,0,0.6)] space-y-3 [&_.card-field]:min-h-[42px] [&_.card-field]:p-2.5 [&_.card-field]:bg-black/60 [&_.card-field]:border [&_.card-field]:border-white/10 [&_.card-field]:rounded-lg">
                            <PayPalCardFieldsProvider
                              createOrder={() => handleCreateOrderForSdk(inlineCheckoutPlan)}
                              onApprove={async (data: any) => {
                                if (data?.orderID) {
                                  await handleCaptureOrderForSdk(data.orderID, inlineCheckoutPlan);
                                }
                              }}
                              onError={(err: any) => {
                                console.error('[PayPalCardFields] Error:', err);
                              }}
                              style={cardFieldStyles}
                            >
                              <PayPalCardFieldsForm className="space-y-3" />
                              <InlineCardSubmitButton
                                amountUsd={planMeta.amountUsd}
                                planTitle={planMeta.title}
                                onDone={() => {}}
                                onError={(err) => setActionMessage({ type: 'error', text: err?.message || 'Card payment submission failed' })}
                              />
                            </PayPalCardFieldsProvider>
                          </div>
                        </div>

                        {/* Section B: In-Page PayPal Buttons Popup */}
                        <div className="relative my-5">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-white/[0.06]"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="bg-[oklch(0.20_0.01_270)] px-4 text-slate-400 font-mono text-[10px] uppercase tracking-wider">
                              or pay with PayPal / in-page popup
                            </span>
                          </div>
                        </div>

                        <div>
                          <PayPalButtons
                            style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 44 }}
                            createOrder={() => handleCreateOrderForSdk(inlineCheckoutPlan)}
                            onApprove={async (data: any) => {
                              if (data?.orderID) {
                                await handleCaptureOrderForSdk(data.orderID, inlineCheckoutPlan);
                              }
                            }}
                            onError={(err: any) => {
                              console.error('[PayPalButtons] Error:', err);
                            }}
                          />
                        </div>
                      </PayPalScriptProvider>

                      {/* Fallback Option */}
                      <div className="pt-3 border-t border-white/[0.06] text-center">
                        <button
                          type="button"
                          onClick={() => handleInitiatePayPalCheckout(inlineCheckoutPlan)}
                          className="text-[11px] font-mono text-slate-400 hover:text-cyan-400 transition flex items-center justify-center gap-1.5 mx-auto cursor-pointer"
                        >
                          <span>Having trouble with embedded card inputs?</span>
                          <span className="underline flex items-center gap-0.5">
                            Open external PayPal checkout tab <ExternalLink size={11} />
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 rounded-2xl liquid-glass-card text-center space-y-3">
                      <RefreshCw size={24} className="animate-spin text-cyan-400 mx-auto" />
                      <p className="text-slate-300 font-mono text-xs">Loading secure payment gateway...</p>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <>
                {/* Section Header: Sovereign Licenses & Executive Upgrades */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
                    <div>
                      <div className="text-xs font-mono font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
                        <Zap size={14} className="fill-amber-400 text-amber-400" />
                        <span>SOVEREIGN LICENSES & EXECUTIVE UPGRADES</span>
                      </div>
                      <div className="text-xs text-slate-400 font-sans mt-0.5">
                        Instant ledger activation via PayPal REST API Webhooks or automated bank validation
                      </div>
                    </div>
                  </div>

                  {/* 3-Column Plan Pricing Grid (Ultra-Compact Executive Suite) */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 items-stretch">
                    
                    {/* Plan 1: Lifetime Setup License */}
                    <div id="kangavena-setup-license-card" className={`bg-gradient-to-b from-[#0e121d] to-[#080b12] border rounded-xl p-3.5 sm:p-4 flex flex-col justify-between transition-all relative ${
                      isUnlimited 
                        ? 'border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.1)]' 
                        : 'border-white/[0.09] hover:border-white/[0.2] hover:shadow-lg'
                    }`}>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[8.5px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-slate-800/90 text-slate-300 border border-slate-700 tracking-wider">
                            LIFETIME BASE
                          </span>
                          {isUnlimited ? (
                            <span className="text-[8.5px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                              <Check size={9} /> Active Plan
                            </span>
                          ) : (
                            <span className="text-[8.5px] font-mono text-slate-400">One-Time License</span>
                          )}
                        </div>

                        <h4 className="text-[15px] font-bold text-white font-sans tracking-tight">Setup License</h4>
                        <p className="text-[10.5px] text-slate-400 mt-0.5 leading-tight truncate">
                          One-time activation unlocking full platform capabilities and unlimited tokens.
                        </p>

                        <div className="my-2 p-2 rounded-lg bg-slate-950/80 border border-white/[0.06] flex items-baseline justify-between gap-2">
                          <div className="text-lg font-bold font-mono font-tabular text-white flex items-baseline gap-1">
                            <span>{selectedCurrency === 'LKR' ? 'Rs. 10,000' : '$33.50'}</span>
                            <span className="text-[9.5px] text-slate-400 font-normal font-sans">/ once</span>
                          </div>
                          <div className="text-[9.5px] font-mono text-amber-400/90 font-medium shrink-0">
                            {selectedCurrency === 'LKR' ? '~ $33.50 USD' : '~ Rs. 10,000 LKR'}
                          </div>
                        </div>

                        <ul className="space-y-1 text-[10.5px] text-slate-300 border-t border-white/[0.06] pt-2">
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span><strong>Unlimited Tokens</strong> (Zero Cap Forever)</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span>All visual flow canvas nodes unlocked</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span>24/7 Live WhatsApp Autonomous Gateway</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span>Includes first 30 days cloud hosting</span>
                          </li>
                        </ul>
                      </div>

                      <div className="pt-2.5">
                        {isUnlimited ? (
                          <div className="w-full py-2 rounded-lg bg-white/[0.03] border border-white/10 text-slate-400 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-default">
                            <Check size={12} className="text-emerald-400" />
                            <span>License Installed & Active</span>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={processingPlan !== null}
                              onClick={() => setInlineCheckoutPlan('setup_license')}
                              className="w-full py-2 rounded-lg bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.15] text-white text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                            >
                              {processingPlan === 'setup_license' && <RefreshCw size={11} className="animate-spin text-amber-400" />}
                              <CreditCard size={12} />
                              <span>Upgrade via Card / PayPal ({selectedCurrency === 'LKR' ? 'Rs. 10,000' : '$33.50'})</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedBankPlan('setup_license');
                                setActiveTab('bank_transfer');
                              }}
                              className="w-full mt-1 text-center text-[9.5px] font-mono text-slate-400 hover:text-amber-300 transition cursor-pointer"
                            >
                              Pay via Sri Lanka Bank Deposit →
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Plan 2: White-Label Add-on */}
                    <div className={`bg-gradient-to-b from-[#0e121d] to-[#080b12] border rounded-xl p-3.5 sm:p-4 flex flex-col justify-between transition-all relative ${
                      isWhiteLabel 
                        ? 'border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.1)]' 
                        : 'border-white/[0.09] hover:border-white/[0.2] hover:shadow-lg'
                    }`}>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[8.5px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 tracking-wider">
                            SOVEREIGN BRANDING
                          </span>
                          <span className="text-[8.5px] font-mono text-slate-400">
                            Standalone Add-on
                          </span>
                        </div>

                        <h4 className="text-[15px] font-bold text-white font-sans tracking-tight">White-Label Add-on</h4>
                        <p className="text-[10.5px] text-slate-400 mt-0.5 leading-tight truncate">
                          Remove all "Luna AI" branding and inject your organization's sovereign identity.
                        </p>

                        <div className="my-2 p-2 rounded-lg bg-slate-950/80 border border-white/[0.06] flex items-baseline justify-between gap-2">
                          <div className="text-lg font-bold font-mono font-tabular text-white flex items-baseline gap-1">
                            <span>{selectedCurrency === 'LKR' ? 'Rs. 5,000' : '$16.75'}</span>
                            <span className="text-[9.5px] text-slate-400 font-normal font-sans">/ once</span>
                          </div>
                          <div className="text-[9.5px] font-mono text-amber-400/90 font-medium shrink-0">
                            {selectedCurrency === 'LKR' ? '~ $16.75 USD' : '~ Rs. 5,000 LKR'}
                          </div>
                        </div>

                        <ul className="space-y-1 text-[10.5px] text-slate-300 border-t border-white/[0.06] pt-2">
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-amber-400 shrink-0" />
                            <span>Custom Agent Master Name</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-amber-400 shrink-0" />
                            <span>Custom 3D & Photo Avatar Upload</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-amber-400 shrink-0" />
                            <span>Embeddable Web Widget Rebranding</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-amber-400 shrink-0" />
                            <span>Web Scraper Custom AI Persona</span>
                          </li>
                        </ul>
                      </div>

                      <div className="pt-2.5">
                        {isWhiteLabel ? (
                          <div className="w-full py-2 rounded-lg bg-white/[0.03] border border-white/10 text-slate-400 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-default">
                            <Check size={12} className="text-purple-400" />
                            <span>White-Label Installed & Active</span>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={processingPlan !== null}
                              onClick={() => setInlineCheckoutPlan('white_label')}
                              className="w-full py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-[0_0_15px_rgba(245,158,11,0.25)] transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                              {processingPlan === 'white_label' && <RefreshCw size={11} className="animate-spin text-slate-950" />}
                              <span>Unlock Rebranding ({selectedCurrency === 'LKR' ? 'Rs. 5,000' : '$16.75'})</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedBankPlan('white_label');
                                setActiveTab('bank_transfer');
                              }}
                              className="w-full mt-1 text-center text-[9.5px] font-mono text-slate-400 hover:text-amber-300 transition cursor-pointer"
                            >
                              Pay via Sri Lanka Bank Deposit →
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Plan 3: Complete Pro Bundle (The Sovereign Executive Suite) */}
                    <div className="bg-gradient-to-b from-[#13121d] via-[#0d101a] to-[#090c14] border border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.15)] rounded-xl p-3.5 sm:p-4 flex flex-col justify-between relative transition-all">
                      {/* Floating Top VIP Ribbon */}
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#16130a] border border-amber-500/60 text-amber-300 text-[8.5px] font-bold font-mono tracking-wider px-2.5 py-0.5 rounded-full shadow-[0_0_15px_rgba(245,158,11,0.35)] flex items-center gap-1 whitespace-nowrap">
                        <Star size={9} className="fill-amber-400 text-amber-400" />
                        <span>EXECUTIVE VIP SUITE • SAVE 25%</span>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1.5 mt-0.5">
                          <span className="text-[8.5px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 tracking-wider">
                            COMPLETE SOVEREIGN BUNDLE
                          </span>
                        </div>

                        <h4 className="text-[15px] font-bold text-amber-300 font-sans tracking-tight">Unlimited + White-Label</h4>
                        <p className="text-[10.5px] text-slate-300 mt-0.5 leading-tight truncate">
                          The definitive suite: All platform features, unlimited tokens, and sovereign branding.
                        </p>

                        <div className="my-2 p-2 rounded-lg bg-slate-950/80 border border-amber-500/20 flex items-baseline justify-between gap-2">
                          <div className="text-lg font-bold font-mono font-tabular text-white flex items-baseline gap-1">
                            <span>{selectedCurrency === 'LKR' ? 'Rs. 15,000' : '$49.99'}</span>
                            <span className="text-slate-400 text-[9.5px] font-normal font-sans">/ full</span>
                          </div>
                          <div className="text-[9.5px] font-mono text-emerald-400 font-semibold shrink-0">
                            {selectedCurrency === 'LKR' ? '~ $49.99 USD' : '~ Rs. 15,000 LKR'}
                          </div>
                        </div>

                        <ul className="space-y-1 text-[10.5px] text-slate-200 border-t border-white/10 pt-2">
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span><strong>Unlimited Tokens Forever</strong> (Zero Cap)</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span><strong>Full White-Label Rights</strong> Unlocked</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span>Custom 3D Avatar & Corporate Identity</span>
                          </li>
                          <li className="flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-400 shrink-0" />
                            <span>30 Days Dedicated Cloud Maintenance</span>
                          </li>
                        </ul>
                      </div>

                      <div className="pt-2.5">
                        {isUnlimited && isWhiteLabel ? (
                          <div className="w-full py-2 rounded-lg bg-white/[0.03] border border-white/10 text-slate-400 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-default">
                            <Check size={12} className="text-emerald-400" />
                            <span>Full Suite Installed & Active</span>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={processingPlan !== null}
                              onClick={() => setInlineCheckoutPlan('full_bundle')}
                              className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-400 to-teal-400 hover:from-emerald-300 hover:to-teal-300 text-slate-950 font-black text-xs shadow-[0_0_20px_rgba(52,211,153,0.35)] transition-all flex items-center justify-center gap-1.5 cursor-pointer tracking-wide"
                            >
                              {processingPlan === 'full_bundle' && <RefreshCw size={11} className="animate-spin text-slate-950" />}
                              <CreditCard size={12} />
                              <span>Upgrade via Card / PayPal ({selectedCurrency === 'LKR' ? 'Rs. 15,000' : '$49.99'})</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedBankPlan('full_bundle');
                                setActiveTab('bank_transfer');
                              }}
                              className="w-full mt-1 text-center text-[9.5px] font-mono text-slate-400 hover:text-emerald-300 transition cursor-pointer"
                            >
                              Pay via Sri Lanka Bank Deposit (LKR) →
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly Cloud AI Maintenance Subscription */}
                <div className="liquid-glass-card rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="space-y-1.5 max-w-xl">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        ONGOING HOSTING
                      </span>
                      <h4 className="text-sm font-bold text-white font-mono">Monthly Cloud AI Maintenance Subscription</h4>
                    </div>
                    <p className="text-xs text-slate-300 font-sans leading-relaxed">
                      Covers 24/7 Luna Neural Live multimodal inference, WhatsApp autonomous co-pilot, SIP telephony voice sockets, and high-speed RAG vector memory.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
                    <div className="text-left sm:text-right font-mono">
                      <div className="text-xl font-bold font-tabular text-emerald-300">
                        {pricing?.maintenanceMonthly?.displayFormatted || `${currSym}2,500`}
                        <span className="text-xs text-slate-400 font-normal font-sans"> / mo</span>
                      </div>
                      {accountStatus?.monthlyDueDate && (
                        <div className="text-[10px] text-slate-400 font-tabular mt-0.5">
                          Due: {new Date(accountStatus.monthlyDueDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    {!isSetupFeePaid ? (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <div className="px-3.5 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-mono flex items-center gap-2">
                          <Lock size={13} className="text-amber-400 shrink-0" />
                          <span>Setup License Required First</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const el = document.getElementById('kangavena-setup-license-card');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="px-4 py-2 rounded-xl text-xs font-mono font-bold bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.12] text-white transition flex items-center gap-1.5 cursor-pointer shadow-md"
                        >
                          <span>Upgrade License ↑</span>
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled={processingPlan !== null}
                          onClick={() => setInlineCheckoutPlan('monthly_subscription')}
                          className="px-5 py-2.5 rounded-xl text-xs font-mono font-bold bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.12] text-white transition shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15)] flex items-center justify-center gap-2 cursor-pointer hover:border-emerald-400/40"
                        >
                          {processingPlan === 'monthly_subscription' && <RefreshCw size={13} className="animate-spin text-emerald-400" />}
                          <CreditCard size={13} />
                          <span>Pay with Card / PayPal ({pricing?.maintenanceMonthly?.displayFormatted || 'Rs. 2,500'})</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setSelectedBankPlan('monthly_subscription');
                            setActiveTab('bank_transfer');
                          }}
                          className="px-4 py-2.5 rounded-xl text-xs font-mono font-bold bg-black/40 hover:bg-black/60 text-amber-300 border border-amber-500/30 transition flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <Building2 size={13} className="text-amber-400" />
                          <span>Bank Slip</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TAB 2: DIRECT BANK TRANSFER (SRI LANKA)                         */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {activeTab === 'bank_transfer' && (
          <div className="space-y-6">
            
            {/* Status Banner: Pending Verification */}
            {accountStatus?.pendingSlip && accountStatus.pendingSlip.status === 'pending_verification' && (
              <div className="liquid-glass-card rounded-2xl p-5 border-amber-500/40 shadow-[inset_0_1px_0_0_rgba(245,158,11,0.2)] flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-in fade-in">
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 shrink-0 mt-0.5">
                    <Hourglass size={20} className="animate-spin" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-white">Bank Deposit Slip Under Review</h4>
                      <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold">
                        PENDING VERIFICATION
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 mt-1">
                      We received your slip for <strong className="text-amber-300">{accountStatus.pendingSlip.planName}</strong> ({accountStatus.pendingSlip.currency} {accountStatus.pendingSlip.amount}). Our finance team verifies slips within 15–30 minutes.
                    </p>
                    <div className="text-[11px] font-mono text-slate-400 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span>Ref No: <strong className="text-white font-tabular">{accountStatus.pendingSlip.referenceNumber}</strong></span>
                      <span>Depositor: <strong className="text-white">{accountStatus.pendingSlip.depositorName}</strong></span>
                      <span>Submitted: {new Date(accountStatus.pendingSlip.submittedAt).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>

                {accountStatus.pendingSlip.slipUrl && (
                  <a
                    href={accountStatus.pendingSlip.slipUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 rounded-xl bg-black/40 hover:bg-black/60 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 font-mono text-xs shrink-0 self-start sm:self-center transition"
                  >
                    <Eye size={13} /> View Uploaded Slip
                  </a>
                )}
              </div>
            )}

            {/* Status Banner: Rejected Slip Notice */}
            {accountStatus?.pendingSlip && accountStatus.pendingSlip.status === 'rejected' && (
              <div className="liquid-glass-card rounded-2xl p-5 border-rose-500/40 text-rose-200 shadow-[inset_0_1px_0_0_rgba(244,63,94,0.2)] flex items-start gap-3">
                <AlertTriangle size={20} className="text-rose-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-rose-200">Previous Slip Verification Failed</h4>
                  <p className="text-xs text-rose-300/90 mt-0.5">
                    {accountStatus.pendingSlip.rejectionReason || 'The uploaded receipt was unreadable or transaction reference did not match.'}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Please double-check your transfer reference and upload a clear screenshot or receipt below.
                  </p>
                </div>
              </div>
            )}

            {/* Section 1: Official Bank Account Card */}
            <div className="liquid-glass-card rounded-2xl p-6 space-y-4 relative overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-cyan-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]">
                    <Landmark size={18} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white font-mono uppercase tracking-tight">
                      Official Bank Account (Sri Lanka)
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Transfer via Online Banking App (Commercial Bank, Sampath, BOC, HNB, FriMi), CEFT, or CDM Cash.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCopyAccountNumber}
                  className="px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-white/[0.06] hover:bg-white/[0.1] text-cyan-300 border border-white/[0.1] flex items-center gap-1.5 transition cursor-pointer self-start sm:self-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]"
                >
                  {copiedAcc ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                  <span>{copiedAcc ? 'COPIED TO CLIPBOARD!' : 'COPY ACC NUMBER'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-1 text-xs font-mono">
                <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Bank Name</div>
                  <div className="text-white font-bold mt-0.5">{bankDetails.bankName}</div>
                </div>
                <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Account Name</div>
                  <div className="text-white font-bold mt-0.5">{bankDetails.accountName}</div>
                </div>
                <div className="p-3.5 rounded-xl bg-black/60 border border-cyan-500/30 shadow-[inset_0_1px_2px_rgba(0,0,0,0.6)]">
                  <div className="text-[10px] text-cyan-400 uppercase font-bold tracking-wider">Account Number</div>
                  <div className="text-cyan-300 text-sm font-bold font-tabular mt-0.5 tracking-wider">{bankDetails.accountNumber}</div>
                </div>
                <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Branch & Swift</div>
                  <div className="text-white font-bold mt-0.5">{bankDetails.branch}</div>
                </div>
              </div>

              <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-2">
                <span className="text-amber-400">💡 Important:</span>
                <span>Please mention your <strong className="text-slate-200">Business Name</strong> or <strong className="text-slate-200">Registered Phone</strong> in the transfer remark so our system identifies your payment instantly.</span>
              </div>
            </div>

            {/* Section 2: Deposit Slip Submission Form */}
            <form onSubmit={handleBankSlipSubmit} className="liquid-glass-card rounded-2xl p-6 space-y-5">
              <div className="border-b border-white/[0.06] pb-3 flex items-center justify-between">
                <h4 className="text-xs font-mono font-bold text-white uppercase flex items-center gap-2">
                  <FileCheck size={14} className="text-cyan-400" />
                  <span>Submit Payment Proof / Deposit Slip</span>
                </h4>
                <span className="text-[10px] text-slate-400 font-mono">Step 2: Verification Form</span>
              </div>

              {/* Package Selector */}
              <div>
                <label className="block text-[11px] font-mono text-slate-300 font-bold uppercase mb-2">
                  Select Package You Paid For:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { id: 'full_bundle', title: 'Complete Pro Bundle', price: 'Rs. 15,000', note: 'Unlimited + White-Label', badge: 'POPULAR', locked: false },
                    { id: 'setup_license', title: 'Setup License', price: 'Rs. 10,000', note: 'Unlimited Tokens', badge: null, locked: false },
                    { id: 'white_label', title: 'White-Label Add-on', price: 'Rs. 5,000', note: 'Branding Rights', badge: null, locked: false },
                    { id: 'monthly_subscription', title: 'Monthly Maintenance', price: 'Rs. 2,500', note: '30 Days Cloud Sync', badge: null, locked: !isSetupFeePaid }
                  ].map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      disabled={pkg.locked}
                      onClick={() => {
                        if (!pkg.locked) setSelectedBankPlan(pkg.id as any);
                      }}
                      className={`p-3.5 rounded-xl border text-left transition-all relative ${
                        pkg.locked
                          ? 'bg-black/30 border-white/[0.04] opacity-40 cursor-not-allowed'
                          : selectedBankPlan === pkg.id
                          ? 'liquid-glass-overlay border-cyan-400/60 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15)] cursor-pointer'
                          : 'liquid-glass-card liquid-glass-card-interactive cursor-pointer'
                      }`}
                    >
                      {pkg.locked ? (
                        <span className="absolute top-2 right-2 text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-0.5">
                          <Lock size={8} /> SETUP REQ
                        </span>
                      ) : pkg.badge ? (
                        <span className="absolute top-2 right-2 text-[8px] font-mono font-bold px-1.5 py-0.2 rounded bg-amber-500 text-slate-950">
                          {pkg.badge}
                        </span>
                      ) : null}
                      <div className="font-bold text-white text-xs">{pkg.title}</div>
                      <div className="text-cyan-300 font-mono font-tabular font-bold text-sm mt-0.5">{pkg.price}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{pkg.note}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid Inputs: Depositor Name & Reference Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-mono text-slate-300 font-bold uppercase mb-1.5">
                    Depositor / Business Name <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Kamal Perera / Tech Solutions"
                    value={depositorName}
                    onChange={(e) => setDepositorName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/[0.08] text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 font-sans text-xs shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)] transition"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-mono text-slate-300 font-bold uppercase mb-1.5">
                    Bank Transaction Reference / Ref No <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. TXN1039824 / CDM Slip No"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/[0.08] text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 font-mono font-tabular text-xs shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)] transition"
                  />
                </div>
              </div>

              {/* Upload File Zone with Preview */}
              <div>
                <label className="block text-[11px] font-mono text-slate-300 font-bold uppercase mb-1.5">
                  Upload Bank Deposit Slip / Transfer Screenshot
                </label>
                <div className="border-2 border-dashed border-white/[0.1] hover:border-cyan-400/40 rounded-2xl p-6 bg-black/30 text-center transition">
                  {slipPreview ? (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <img
                        src={slipPreview}
                        alt="Slip Preview"
                        className="max-h-36 rounded-xl border border-white/[0.15] object-contain shadow-lg"
                      />
                      <div className="text-left space-y-1.5">
                        <div className="text-xs text-emerald-400 font-bold flex items-center gap-1.5">
                          <CheckCircle2 size={14} /> Slip attached successfully
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono">
                          {slipFile ? slipFile.name : 'Image loaded'}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSlipFile(null);
                            setSlipPreview(null);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[10px] font-mono hover:bg-rose-500/25 transition cursor-pointer"
                        >
                          Remove / Change Slip
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center justify-center py-4">
                      <UploadCloud size={32} className="text-cyan-400 mb-2" />
                      <span className="text-xs text-white font-bold">
                        Click to browse or drag & drop slip photo
                      </span>
                      <span className="text-[10px] text-slate-400 mt-1">
                        JPEG, PNG, WEBP, or PDF (Max 12MB)
                      </span>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleSlipFileChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Optional Note */}
              <div>
                <label className="block text-[11px] font-mono text-slate-400 mb-1.5">
                  Optional Note / Message to Finance Team:
                </label>
                <input
                  type="text"
                  placeholder="Any specific note regarding this payment..."
                  value={slipNotes}
                  onChange={(e) => setSlipNotes(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/[0.08] text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 font-sans text-xs shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)] transition"
                />
              </div>

              {/* Submit Action */}
              <button
                type="submit"
                disabled={isSubmittingSlip}
                className="w-full py-3.5 rounded-xl text-xs font-mono font-bold bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 shadow-[inset_0_1px_0_0_rgba(245,158,11,0.3)] transition flex items-center justify-center gap-2 cursor-pointer"
              >
                {isSubmittingSlip ? (
                  <>
                    <RefreshCw size={14} className="animate-spin text-amber-400" />
                    <span>Submitting Slip...</span>
                  </>
                ) : (
                  <>
                    <Building2 size={14} />
                    <span>Submit Slip for Verification</span>
                  </>
                )}
              </button>
            </form>

          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TAB 3: INVOICES & TRANSACTION HISTORY                           */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            <div className="liquid-glass-card rounded-2xl overflow-hidden shadow-xl">
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
                <h4 className="text-xs font-mono font-bold text-white uppercase flex items-center gap-2">
                  <Receipt size={14} className="text-cyan-400" />
                  <span>Verified Payment Receipts & Invoices</span>
                </h4>
                <span className="text-[10px] font-mono text-slate-400">
                  Automated PayPal REST Audit Trail
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-white/[0.06] bg-white/[0.02] font-mono text-[10px] text-slate-400 uppercase tracking-wider">
                      <th className="py-3 px-4">Date & Time</th>
                      <th className="py-3 px-4">Purchased Item</th>
                      <th className="py-3 px-4">Amount</th>
                      <th className="py-3 px-4">Method</th>
                      <th className="py-3 px-4">PayPal Order ID</th>
                      <th className="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04] font-mono">
                    {!accountStatus?.transactions || accountStatus.transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-500 text-xs">
                          No transactions recorded yet. Payments completed via PayPal will log here in real-time.
                        </td>
                      </tr>
                    ) : (
                      accountStatus.transactions.map((txn, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02] transition">
                          <td className="py-3 px-4 text-slate-300 text-[11px] font-tabular">
                            {txn.date ? new Date(txn.date).toLocaleString() : 'Recent'}
                          </td>
                          <td className="py-3 px-4 font-bold text-white font-sans">
                            {txn.planType === 'setup_license' ? 'Setup License' :
                             txn.planType === 'whitelabel_addon' ? 'White-Label Add-on' :
                             txn.planType === 'complete_pro_bundle' ? 'Complete Pro Bundle' :
                             txn.planType === 'maintenance_monthly' ? 'Monthly Maintenance' :
                             txn.description || txn.planType}
                          </td>
                          <td className="py-3 px-4 text-emerald-400 font-bold font-tabular">
                            ${Number(txn.amountUsd || txn.amount || 0).toFixed(2)} USD
                            {txn.amountLkr && <span className="text-slate-400 text-[10px] block font-sans font-tabular">Rs. {Number(txn.amountLkr).toLocaleString()}</span>}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-300 border border-blue-500/30">
                              {txn.method || 'PayPal REST'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-[11px] truncate max-w-[140px] font-tabular" title={txn.orderId}>
                            {txn.orderId || 'DIRECT_GRANT'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 w-fit">
                              <CheckCircle2 size={10} />
                              <span>{txn.status ? txn.status.toUpperCase() : 'COMPLETED'}</span>
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: TOKEN USAGE & MONTHLY BILLING CYCLE HISTORY */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {activeTab === 'token_history' && (
          <div className="space-y-6">
            {/* 1. ARCHITECTURE & CYCLE NOTICE BANNER */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-cyan-950/40 via-slate-900 to-indigo-950/40 border border-cyan-500/30 shadow-xl flex items-start gap-3.5">
              <div className="p-2 rounded-xl bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 shrink-0 mt-0.5">
                <Clock size={18} />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider">
                    Sovereign Token Accounting & Auto-Resetting Monthly Cycles
                  </h4>
                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 text-[9px] font-mono font-bold">
                    3-DAY SETUP GRACE WINDOW + MONTHLY RENEWAL
                  </span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed font-sans">
                  Kangavena accounts begin with an initial 3-day Setup & Grace window upon registration (<code className="text-cyan-300 font-mono text-[11px]">Cycle 0: Rs. 10,000 Setup License</code>). The official recurring monthly billing cycle initiates exactly 3 days after creation, and active token counters automatically roll over and reset at each month's renewal date (<strong className="text-amber-400 font-mono">Next Due: {tokenHistory?.nextDueDate || accountStatus?.monthlyDueDate || 'Pending'}</strong>). All previous cycle records remain permanently archived below with full disaggregated audit fidelity.
                </p>
              </div>
            </div>

            {/* 2. ACTIVE CYCLE HUD & ALL-TIME VAULT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* CURRENT ACTIVE CYCLE CARD */}
              <div className="liquid-glass-card rounded-2xl p-5 border border-cyan-500/30 shadow-xl space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs font-mono font-bold text-white uppercase tracking-wide">
                      {tokenHistory?.activeCycle?.label || 'Current Monthly Billing Cycle'}
                    </span>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[9px] font-mono font-bold flex items-center gap-1">
                    <CheckCircle2 size={10} />
                    <span>{tokenHistory?.activeCycle?.status || 'ACTIVE CYCLE'}</span>
                  </span>
                </div>

                <div className="bg-black/50 p-3.5 rounded-xl border border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                    <span>Cycle Period:</span>
                    <strong className="text-cyan-300">
                      {tokenHistory?.activeCycle?.startDate || 'Recent'} ➔ {tokenHistory?.activeCycle?.endDate || 'Pending'}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                    <span>Days to Monthly Renewal:</span>
                    <strong className="text-amber-300">
                      {tokenHistory?.activeCycle?.daysUntilDue !== null && tokenHistory?.activeCycle?.daysUntilDue !== undefined
                        ? tokenHistory.activeCycle.daysUntilDue > 0
                          ? `${tokenHistory.activeCycle.daysUntilDue} Days Remaining`
                          : 'Due Today'
                        : 'Active'}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                    <span>Monthly Cloud AI Fee:</span>
                    <strong className="text-emerald-400">Rs. 2,500 LKR ($8.30 USD)</strong>
                  </div>
                </div>

                {/* Cycle Token Stats Grid */}
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="p-3 rounded-xl bg-emerald-950/20 border border-emerald-500/30">
                    <div className="text-[10px] text-emerald-400 font-bold uppercase">Free Pool Tokens</div>
                    <div className="text-lg font-black text-white mt-1">
                      {Number(tokenHistory?.activeCycle?.tokens?.freeTokens || 0).toLocaleString()}
                    </div>
                    <div className="text-[9px] text-emerald-300 font-bold mt-0.5">
                      🎉 Saved: Rs. {Number(tokenHistory?.activeCycle?.tokens?.freeSavedLkr || 0).toFixed(2)}
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-amber-950/20 border border-amber-500/30">
                    <div className="text-[10px] text-amber-400 font-bold uppercase">Paid Master Tokens</div>
                    <div className="text-lg font-black text-white mt-1">
                      {Number(tokenHistory?.activeCycle?.tokens?.paidTokens || 0).toLocaleString()}
                    </div>
                    <div className="text-[9px] text-amber-300 font-bold mt-0.5">
                      Billable: Rs. {Number(tokenHistory?.activeCycle?.tokens?.billableCostLkr || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5">
                  <RefreshCw size={11} className="text-cyan-400 shrink-0" />
                  <span>Tokens in this cycle will automatically reset on {tokenHistory?.activeCycle?.endDate || 'renewal date'}.</span>
                </div>
              </div>

              {/* LIFETIME CUMULATIVE VAULT CARD */}
              <div className="liquid-glass-card rounded-2xl p-5 border border-purple-500/30 shadow-xl space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-purple-400" />
                    <span className="text-xs font-mono font-bold text-white uppercase tracking-wide">
                      All-Time Lifetime Token Ledger
                    </span>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 text-[9px] font-mono font-bold">
                    ZERO TRUNCATION VAULT
                  </span>
                </div>

                <div className="bg-black/50 p-3.5 rounded-xl border border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                    <span>Account Inception:</span>
                    <strong className="text-white">
                      {tokenHistory?.createdAt ? new Date(tokenHistory.createdAt).toLocaleDateString() : 'N/A'}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                    <span>Total Lifetime Tokens:</span>
                    <strong className="text-cyan-300 text-sm">
                      {Number(tokenHistory?.allTimeUsage?.totalTokens || 0).toLocaleString()}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                    <span>Lifetime Free Pool Ratio:</span>
                    <strong className="text-emerald-400">100% Sovereign Free</strong>
                  </div>
                </div>

                {/* Lifetime Metrics Grid */}
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="p-3 rounded-xl bg-purple-950/20 border border-purple-500/30">
                    <div className="text-[10px] text-purple-400 font-bold uppercase">Lifetime LKR Saved</div>
                    <div className="text-lg font-black text-emerald-400 mt-1">
                      Rs. {Number(tokenHistory?.allTimeUsage?.freeSavedLkr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">
                      Across all customer chats & calls
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-blue-950/20 border border-blue-500/30">
                    <div className="text-[10px] text-blue-400 font-bold uppercase">Total Cycles Archived</div>
                    <div className="text-lg font-black text-white mt-1">
                      {tokenHistory?.cycles?.length || 1} Cycles
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">
                      Immutable DB persistence
                    </div>
                  </div>
                </div>

                <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5">
                  <ShieldCheck size={11} className="text-emerald-400 shrink-0" />
                  <span>Permanent audit trail guaranteed. Oldest conversations are never truncated or lost.</span>
                </div>
              </div>
            </div>

            {/* 3. MONTHLY CLOUD AI MAINTENANCE PAYMENT MODULE (RS. 2,500 LKR / $8.30 USD) */}
            <div className="liquid-glass-card rounded-2xl p-6 border-2 border-amber-500/40 shadow-2xl bg-gradient-to-r from-amber-950/30 via-slate-900 to-emerald-950/30">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-2 max-w-xl">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CreditCard className="w-5 h-5 text-amber-400" />
                    <h3 className="text-sm font-black text-white font-mono uppercase tracking-wider">
                      Monthly Cloud AI Maintenance Payment (Rs. 2,500 / month)
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[9px] font-mono font-bold">
                      RECURRING CLOUD HOSTING
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 font-sans leading-relaxed">
                    Powers your 24/7 dedicated Kangavena Cloud container runtime, continuous Luna Neural Multimodal engine hosting, real-time WhatsApp SIP voice gateway, vector memory sync, and automatic backups. Payable on your monthly billing date (<strong className="text-amber-300 font-mono">{tokenHistory?.nextDueDate || accountStatus?.monthlyDueDate || 'Pending'}</strong>).
                  </p>
                  <div className="flex items-center gap-4 text-xs font-mono text-slate-400 pt-1">
                    <span>Due Date: <strong className="text-white">{tokenHistory?.nextDueDate || accountStatus?.monthlyDueDate || 'Cycle Renewal'}</strong></span>
                    <span>•</span>
                    <span>Fee: <strong className="text-emerald-400 font-bold">Rs. 2,500 LKR ($8.30 USD)</strong></span>
                  </div>
                </div>

                {/* Quick Payment Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleInitiatePayPalCheckout('monthly_subscription')}
                    disabled={processingPlan === 'monthly_subscription'}
                    className="px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-mono font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {processingPlan === 'monthly_subscription' ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <CreditCard size={14} />
                    )}
                    <span>Pay $8.30 USD via PayPal</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBankPlan('monthly_subscription');
                      setActiveTab('bank_transfer');
                    }}
                    className="px-5 py-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] border border-white/20 text-white font-mono font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Building2 size={14} className="text-emerald-400" />
                    <span>Pay Rs. 2,500 Bank Transfer</span>
                  </button>
                </div>
              </div>
            </div>

            {/* 4. HISTORICAL CYCLE-BY-CYCLE AUDIT TABLE */}
            <div className="liquid-glass-card rounded-2xl overflow-hidden shadow-xl">
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
                <h4 className="text-xs font-mono font-bold text-white uppercase flex items-center gap-2">
                  <Receipt size={14} className="text-cyan-400" />
                  <span>Cycle-by-Cycle Token Usage & Billing History</span>
                </h4>
                <span className="text-[10px] font-mono text-slate-400">
                  Separated Free Key Pool & Master AI Records
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs font-mono">
                  <thead>
                    <tr className="border-b border-white/[0.06] bg-white/[0.02] text-[10px] text-slate-400 uppercase tracking-wider">
                      <th className="py-3 px-4">Cycle</th>
                      <th className="py-3 px-4">Period</th>
                      <th className="py-3 px-4">Total Tokens</th>
                      <th className="py-3 px-4">Free Pool (Saved)</th>
                      <th className="py-3 px-4">Paid Master AI</th>
                      <th className="py-3 px-4">Platform Fee</th>
                      <th className="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {!tokenHistory?.cycles || tokenHistory.cycles.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-slate-500 text-xs">
                          {loadingHistory ? 'Loading cycle token audit ledger...' : 'No cycle records found.'}
                        </td>
                      </tr>
                    ) : (
                      tokenHistory.cycles.map((c: any, idx: number) => (
                        <tr key={idx} className="hover:bg-white/[0.02] transition">
                          <td className="py-3 px-4 font-bold text-white">
                            <div className="flex items-center gap-1.5">
                              {c.isSetupGraceCycle ? (
                                <span className="px-2 py-0.5 rounded text-[9px] bg-purple-500/20 text-purple-300 border border-purple-500/40">
                                  Cycle 0 (Setup)
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-[9px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                                  Cycle {c.cycleNumber}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-300 text-[11px] font-tabular">
                            {c.startDate} ➔ {c.endDate}
                          </td>
                          <td className="py-3 px-4 font-bold text-white font-tabular">
                            {Number(c.tokens?.totalTokens || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-emerald-400 font-tabular">
                            <div>{Number(c.tokens?.freeTokens || 0).toLocaleString()}</div>
                            <div className="text-[9px] text-slate-400">
                              Saved: Rs. {Number(c.tokens?.freeSavedLkr || 0).toFixed(2)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-amber-300 font-tabular">
                            <div>{Number(c.tokens?.paidTokens || 0).toLocaleString()}</div>
                            <div className="text-[9px] text-slate-400">
                              Billable: Rs. {Number(c.tokens?.billableCostLkr || 0).toFixed(2)}
                            </div>
                          </td>
                          <td className="py-3 px-4 font-bold text-slate-200 font-tabular">
                            {c.isSetupGraceCycle ? (
                              <span className="text-purple-300">Rs. 10,000 (Setup License)</span>
                            ) : (
                              <span className="text-emerald-400">Rs. 2,500 / mo</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border flex items-center gap-1 w-fit ${
                              c.status === 'ACTIVE'
                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                : 'bg-slate-800 text-slate-400 border-white/10'
                            }`}>
                              {c.status === 'ACTIVE' && <CheckCircle2 size={9} />}
                              <span>{c.status}</span>
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Footer: Luxury Specular Bottom Bar (Stitch Spec) ── */}
      <div className="px-6 py-4 border-t border-white/[0.06] bg-[#090a10] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-[11px] text-slate-400 font-mono shrink-0">
        <div className="flex items-center gap-2">
          <ShieldCheck size={15} className="text-emerald-400 shrink-0" />
          <span>256-bit SSL Vault Encrypted</span>
          <span className="text-slate-600">•</span>
          <span>PayPal REST API Automated Settlement</span>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white transition cursor-pointer font-bold border border-white/10 text-xs font-mono shadow-sm"
        >
          {isFullPage ? 'Close Terminal' : 'Close'}
        </button>
      </div>
    </>
  );

  if (isFullPage) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden bg-[#07080c] text-slate-100">
        {modalBodyContent}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2.5 sm:p-4 bg-black/80 backdrop-blur-2xl animate-in fade-in duration-200">
      <div className="relative w-full max-w-5xl liquid-glass-obsidian rounded-2xl sm:rounded-3xl overflow-hidden text-slate-100 flex flex-col max-h-[96dvh] sm:max-h-[92vh]">
        {modalBodyContent}
      </div>
    </div>
  );
};

export default TenetAccountBillingModal;
